import React, { useState } from 'react';
import WebSpeechPrototype from './WebSpeechPrototype';
import OpenAITTSPrototype from './OpenAITTSPrototype';
import AzureTTSPrototype from './AzureTTSPrototype';

type TTSProvider = 'web-speech' | 'openai' | 'azure' | 'comparison';

/**
 * TTS Comparison Page
 * Allows side-by-side testing of all three TTS providers
 *
 * Purpose: Evaluate which TTS provider(s) to use for Read Aloud feature
 * - Web Speech API (free, browser-native)
 * - OpenAI TTS-1 (premium, $15/1M chars)
 * - Azure Neural TTS (premium, $16/1M chars, 500K free/month)
 */
export const TTSComparisonPage: React.FC = () => {
  const [activeProvider, setActiveProvider] = useState<TTSProvider>('comparison');

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-md sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <h1 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
            🎤 TTS Provider Comparison
          </h1>

          {/* Provider Tabs */}
          <div className="flex gap-2 overflow-x-auto">
            <button
              onClick={() => setActiveProvider('comparison')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                activeProvider === 'comparison'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              📊 Comparison Matrix
            </button>
            <button
              onClick={() => setActiveProvider('web-speech')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                activeProvider === 'web-speech'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              🌐 Web Speech API (Free)
            </button>
            <button
              onClick={() => setActiveProvider('openai')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                activeProvider === 'openai'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              🤖 OpenAI TTS-1 (Premium)
            </button>
            <button
              onClick={() => setActiveProvider('azure')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                activeProvider === 'azure'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              ☁️ Azure Neural TTS (Premium)
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeProvider === 'comparison' && (
          <div className="space-y-8">
            {/* Decision Matrix */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                📊 Decision Matrix
              </h2>

              {/* Comparison Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-300 dark:border-gray-600">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">
                        Criteria
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-green-700 dark:text-green-400">
                        Web Speech API
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-purple-700 dark:text-purple-400">
                        OpenAI TTS-1
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-blue-700 dark:text-blue-400">
                        Azure Neural TTS
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700 dark:text-gray-300">
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <td className="py-3 px-4 font-medium">Cost</td>
                      <td className="py-3 px-4">✅ <strong>$0</strong> (100% free)</td>
                      <td className="py-3 px-4">💰 $15/1M chars (~$0.075/chapter)</td>
                      <td className="py-3 px-4">💰 $16/1M chars (500K free/month)</td>
                    </tr>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <td className="py-3 px-4 font-medium">Voice Quality</td>
                      <td className="py-3 px-4">⚠️ Good (device-dependent)</td>
                      <td className="py-3 px-4">✅ Excellent (natural, consistent)</td>
                      <td className="py-3 px-4">✅ Excellent (natural, SSML)</td>
                    </tr>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <td className="py-3 px-4 font-medium">Offline Support</td>
                      <td className="py-3 px-4">✅ Yes (iOS/Android local voices)</td>
                      <td className="py-3 px-4">❌ No (requires internet)</td>
                      <td className="py-3 px-4">❌ No (requires internet)</td>
                    </tr>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <td className="py-3 px-4 font-medium">Latency</td>
                      <td className="py-3 px-4">✅ &lt;100ms (instant)</td>
                      <td className="py-3 px-4">⚠️ 500-2000ms (API call)</td>
                      <td className="py-3 px-4">⚠️ 500-2000ms (API call)</td>
                    </tr>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <td className="py-3 px-4 font-medium">Available Voices</td>
                      <td className="py-3 px-4">⚠️ 10-50 (device-dependent)</td>
                      <td className="py-3 px-4">✅ 6 premium neural voices</td>
                      <td className="py-3 px-4">✅ 200+ neural voices (50+ langs)</td>
                    </tr>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <td className="py-3 px-4 font-medium">Speed Control</td>
                      <td className="py-3 px-4">✅ 0.5x - 2.0x</td>
                      <td className="py-3 px-4">✅ 0.25x - 4.0x</td>
                      <td className="py-3 px-4">✅ 0.5x - 2.0x</td>
                    </tr>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <td className="py-3 px-4 font-medium">Browser Support</td>
                      <td className="py-3 px-4">✅ Safari, Chrome, Edge, Firefox</td>
                      <td className="py-3 px-4">✅ All (audio playback)</td>
                      <td className="py-3 px-4">✅ All (audio playback)</td>
                    </tr>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <td className="py-3 px-4 font-medium">Scalability</td>
                      <td className="py-3 px-4">✅ Unlimited (client-side)</td>
                      <td className="py-3 px-4">✅ Unlimited (pay-per-use)</td>
                      <td className="py-3 px-4">✅ Unlimited (pay-per-use)</td>
                    </tr>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <td className="py-3 px-4 font-medium">Server Caching</td>
                      <td className="py-3 px-4">❌ Not possible (client-side)</td>
                      <td className="py-3 px-4">✅ Yes (95% cost savings)</td>
                      <td className="py-3 px-4">✅ Yes (95% cost savings)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Recommendation */}
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg shadow-lg p-6 border border-purple-200 dark:border-purple-800">
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
                🎯 Recommended Approach
              </h2>

              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                  <h3 className="font-bold text-lg mb-2 text-purple-700 dark:text-purple-400">
                    ✅ Hybrid Strategy: Free + Premium
                  </h3>
                  <ul className="list-disc list-inside space-y-2 text-sm">
                    <li><strong>Free Tier:</strong> Web Speech API (default for all users)</li>
                    <li><strong>Premium Tier:</strong> OpenAI TTS-1 or Azure Neural TTS (bookstore configurable)</li>
                    <li><strong>Server-Side Caching:</strong> Generate premium audio once, serve to all students (95% cost savings)</li>
                    <li><strong>Bookstore Control:</strong> TtsServiceLevel enum (None, Free, Premium, Both)</li>
                  </ul>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                  <h3 className="font-bold text-lg mb-2 text-blue-700 dark:text-blue-400">
                    💰 Cost Optimization Strategy
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="font-semibold mb-1">Without Caching:</p>
                      <p>1,000 students × 50 chapters × 5,000 chars</p>
                      <p className="text-red-600 dark:text-red-400 font-bold">= $7,500/month 💸</p>
                    </div>
                    <div>
                      <p className="font-semibold mb-1">With Server-Side Caching:</p>
                      <p>Generate once, serve to all</p>
                      <p className="text-green-600 dark:text-green-400 font-bold">= $376/month ✅</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                  <h3 className="font-bold text-lg mb-2 text-green-700 dark:text-green-400">
                    🔒 Security: Chunked Streaming
                  </h3>
                  <ul className="list-disc list-inside space-y-2 text-sm">
                    <li>Stream encrypted 100 KB chunks (not full file)</li>
                    <li>DRM validation on every chunk request</li>
                    <li>Chunks decrypted in-memory only (not saved to disk)</li>
                    <li>Prevents students from downloading and sharing audio files</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Testing Instructions */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
                📋 Testing Instructions
              </h2>

              <div className="space-y-4">
                <div>
                  <h3 className="font-bold mb-2 text-gray-900 dark:text-white">
                    1. Test on All Devices
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                      <p className="font-semibold mb-2">📱 iPhone 16 Pro</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                        <li>Safari browser</li>
                        <li>Check "Samantha (Enhanced)" voice</li>
                        <li>Test offline mode</li>
                        <li>Test background playback</li>
                      </ul>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                      <p className="font-semibold mb-2">📱 Google Pixel 9</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                        <li>Chrome browser</li>
                        <li>Check Google Neural voices</li>
                        <li>Test offline mode</li>
                        <li>Compare vs iPhone quality</li>
                      </ul>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                      <p className="font-semibold mb-2">📱 iPad</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                        <li>Safari browser</li>
                        <li>Larger screen UI testing</li>
                        <li>Test split-screen mode</li>
                        <li>Verify voice quality</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-bold mb-2 text-gray-900 dark:text-white">
                    2. Measure Key Metrics
                  </h3>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 text-sm">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-300 dark:border-gray-600">
                          <th className="text-left py-2">Metric</th>
                          <th className="text-left py-2">Target</th>
                          <th className="text-left py-2">Notes</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-700 dark:text-gray-300">
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <td className="py-2">Latency</td>
                          <td className="py-2">&lt; 300ms</td>
                          <td className="py-2">Time to first audio</td>
                        </tr>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <td className="py-2">Voice Quality</td>
                          <td className="py-2">7-10/10</td>
                          <td className="py-2">Naturalness rating</td>
                        </tr>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <td className="py-2">Audio Size</td>
                          <td className="py-2">&lt; 1 MB/5min</td>
                          <td className="py-2">Bandwidth estimate</td>
                        </tr>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <td className="py-2">Background Playback</td>
                          <td className="py-2">Works</td>
                          <td className="py-2">Lock screen controls</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <h3 className="font-bold mb-2 text-gray-900 dark:text-white">
                    3. Fill Out Evaluation Form
                  </h3>
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-sm text-gray-700 dark:text-gray-300">
                    <p className="mb-2">After testing, rate each provider on:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Voice naturalness (1-10)</li>
                      <li>Latency (fast/acceptable/slow)</li>
                      <li>Offline support (yes/no)</li>
                      <li>Background playback (works/broken)</li>
                      <li>Overall user experience (1-10)</li>
                    </ul>
                    <p className="mt-2 font-semibold">
                      Create a document with your findings to inform the final decision!
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
                🚀 Next Steps After Testing
              </h2>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300">
                <li><strong>Choose providers:</strong> Confirm Web Speech API + (OpenAI or Azure)</li>
                <li><strong>Implement backend:</strong> Create ITTSProvider interface and implementations</li>
                <li><strong>Create entities:</strong> BookChapter, PdfPage, BookstoreSettings (via ABP Suite)</li>
                <li><strong>Build UI:</strong> Player controls, progress bar, speed settings</li>
                <li><strong>Add DRM:</strong> Session validation + chunked streaming</li>
                <li><strong>Deploy & test:</strong> Real-world testing with actual students</li>
              </ol>
            </div>
          </div>
        )}

        {activeProvider === 'web-speech' && <WebSpeechPrototype />}
        {activeProvider === 'openai' && <OpenAITTSPrototype />}
        {activeProvider === 'azure' && <AzureTTSPrototype />}
      </div>
    </div>
  );
};

export default TTSComparisonPage;
